import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { COLORS } from '../../../../shared/colors';
import { ZoomOutMap, Close } from '@mui/icons-material';
import { IconButton, Dialog, DialogContent, DialogTitle, Box } from '@mui/material';
import { ICONS } from '../../../../shared/icons';
import { DATE_PICKER_SHORT_MONTHS } from '../../../../shared/appConstants';

const CustomTooltip = ({ active, payload, chartData }) => {
  const { t } = useTranslation();
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const day = data.day;
    const ideal = data.ideal;
    const actual = data.actual;
    
    let statusColor = COLORS.charts.actualGreen;
    let statusText = t('sprintManagement.onTrack', { defaultValue: 'On Track' });
    
    if (actual > ideal) {
      const index = chartData ? chartData.findIndex(d => d.day === day) : -1;
      if (index > 0 && actual < chartData[index - 1].actual) {
         statusColor = COLORS.charts.actualYellow;
         statusText = t('sprintManagement.recovering', { defaultValue: 'Recovering' });
      } else {
         statusColor = COLORS.charts.actualRed;
         statusText = t('sprintManagement.offTrack', { defaultValue: 'Off Track' });
      }
    }

    return (
      <div className="chart-custom-tooltip">
        <p className="tooltip-label">{day}</p>
        
        <div className="tooltip-row">
          <span className="tooltip-key">{t('sprintManagement.idealTasks')}</span>
          <span className="tooltip-val" style={{ color: COLORS.charts.idealLine }}>{ideal}</span>
        </div>
        
        <div className="tooltip-row">
          <span className="tooltip-key">{t('sprintManagement.actualTasks')}</span>
          <span className="tooltip-val" style={{ color: statusColor }}>{actual}</span>
        </div>
        
        <div className="tooltip-row last-row">
          <span className="tooltip-key">{t('sprintManagement.status', { defaultValue: 'Status' })}</span>
          <span className="tooltip-val" style={{ color: statusColor }}>{statusText}</span>
        </div>
      </div>
    );
  }
  return null;
};

const BurndownChartContent = ({ data }) => {

  // Create SVG linear gradient stops based on data points
  // X-axis is evenly spaced, so offset is (index / (length - 1)) * 100
  const renderGradientStops = () => {
    if (!data || data.length === 0) return null;
    
    // If only 1 data point exists, return a solid color so the dot inherits it properly
    if (data.length === 1) {
      const entry = data[0];
      const color = entry.actual > entry.ideal ? COLORS.charts.actualRed : COLORS.charts.actualGreen;
      return (
        <>
          <stop offset="0%" stopColor={color} />
          <stop offset="100%" stopColor={color} />
        </>
      );
    }
    
    return data.map((entry, index) => {
      const percent = (index / (data.length - 1)) * 100;
      let color = COLORS.charts.actualGreen; // Default: Ahead of or exactly on schedule (Green)
      
      if (entry.actual > entry.ideal) {
        // Behind schedule (Remaining work > Ideal remaining work)
        if (index > 0 && entry.actual < data[index - 1].actual) {
          // Trend moves downward (burning points) but still behind schedule (Yellow)
          color = COLORS.charts.actualYellow;
        } else {
          // Behind schedule and no progress made, or scope increased (Red)
          color = COLORS.charts.actualRed;
        }
      }

      return <stop key={`stop-${index}`} offset={`${percent}%`} stopColor={color} />;
    });
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={data}
        margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
      >
        <defs>
          <linearGradient id="actualLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            {renderGradientStops()}
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.gray[200]} />
        <XAxis dataKey="day" padding={{ left: 20, right: 20 }} tick={{ fill: COLORS.gray[500], fontSize: 12 }} axisLine={{ stroke: COLORS.black }} tickLine={false} />
        <YAxis domain={[0, dataMax => dataMax === 0 ? 1 : dataMax * 1.25]} tick={{ fill: COLORS.gray[500], fontSize: 12 }} axisLine={{ stroke: COLORS.black }} tickLine={false} label={{ value: 'Story Points', position: 'top', offset: 25, fill: COLORS.gray[500], fontSize: 12, fontWeight: 500 }} />
        <Tooltip content={(props) => <CustomTooltip {...props} chartData={data} />} cursor={{ stroke: COLORS.ui.shadowLight, strokeWidth: 2 }} />
        <Line 
          type="monotone" 
          dataKey="ideal" 
          name="Expected Remaining SP" 
          stroke={COLORS.charts.idealLine} 
          strokeWidth={2}
          strokeDasharray="5 5"
          dot={data?.length === 1 ? { r: 4, strokeWidth: 2, fill: COLORS.white, strokeDasharray: 'none' } : false}
          activeDot={data?.length === 1 ? { r: 6, strokeDasharray: 'none' } : false}
          isAnimationActive={false}
        />
        <Line 
          type="monotone" 
          dataKey="actual" 
          name="Actual Remaining SP" 
          stroke="url(#actualLineGradient)" 
          strokeWidth={3}
          dot={{ r: 4, strokeWidth: 2, fill: COLORS.white }}
          activeDot={{ r: 6 }}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

const BurndownChart = ({ sprintId, apiData }) => {
  const { t } = useTranslation();
  const [isFullScreen, setIsFullScreen] = useState(false);

  const burnChartData = apiData?.data?.burn_chart || {};
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${DATE_PICKER_SHORT_MONTHS[date.getMonth()]} ${date.getDate()}`;
  };

  const burndownData = Object.keys(burnChartData).map(dateKey => {
    return {
      day: formatDate(dateKey),
      ideal: burnChartData[dateKey].expected,
      actual: burnChartData[dateKey].achieved
    };
  });

  const handleDownload = async () => {
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { default: jsPDF } = await import('jspdf');
      
      const element = document.querySelector('.burndown-pdf-export');
      if (!element) {
        toast.error(t('sprintManagement.chartNotReady', 'Chart is not ready for export'));
        return;
      }
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: COLORS.white,
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth() - 20; // 10mm margin on each side
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      let y = 10;
      if (pdfHeight < (pdf.internal.pageSize.getHeight() - 20)) {
        y = (pdf.internal.pageSize.getHeight() - pdfHeight) / 2;
      }
      
      pdf.addImage(imgData, 'PNG', 10, y, pdfWidth, pdfHeight);
      pdf.save(`burndown-chart-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(t('sprintManagement.burndownExportSuccess', 'Burndown chart exported successfully.'));
    } catch (error) {
      toast.error(t('sprintManagement.burndownExportFailed', 'Failed to export burndown chart.'));
    }
  };

  return (
    <>
      <div className="sm-card chart-card burndown-card report-export-chart-resized">
        <div className="chart-header">
          <h2 className="card-title">{t('sprintManagement.burndownChartTitle')}</h2>
          <IconButton size="small" onClick={() => setIsFullScreen(true)}>
            <ZoomOutMap fontSize="small" />
          </IconButton>
        </div>
        
        <div className="chart-container">
          {burndownData.length === 0 ? (
            <div className="no-data-msg">
              {t('sprintManagement.noDataAvailable', { defaultValue: 'No data available to display' })}
            </div>
          ) : (
            <BurndownChartContent data={burndownData} />
          )}
        </div>
      </div>

      <Dialog 
        open={isFullScreen} 
        onClose={() => setIsFullScreen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle className="burndown-dialog-title">
          {t('sprintManagement.burndownChartTitle')}
          <Box display="flex" gap={1}>
            <IconButton 
              size="small" 
              onClick={handleDownload}
            >
              <Box component="img" src={ICONS.download} sx={{ width: 20, height: 20 }} />
            </IconButton>
            <IconButton size="small" onClick={() => setIsFullScreen(false)}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className="burndown-pdf-export">
          {burndownData.length === 0 ? (
            <div className="no-data-msg">
              {t('sprintManagement.noDataAvailable', { defaultValue: 'No data available to display' })}
            </div>
          ) : (
            <BurndownChartContent data={burndownData} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BurndownChart;
